import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { HostBridge } from '../../host.js';
import type { Direction } from '../../motion/keyframes.js';
import {
  buildEventMeta,
  resolveEventIdentity,
  trackSurveyEvent,
  type ShellHydration,
} from '../../api.js';
import { BrandingFooter } from './_components/BrandingFooter.js';
import { ConversationalSurvey } from './_components/ConversationalSurvey.js';
import { NudgeTeaser } from './_components/NudgeTeaser.js';
import { PagedSurvey } from './_components/PagedSurvey.js';
import { StatusView } from './_components/StatusView.js';
import { Spinner } from '../../_components/Spinner.js';
import { SurveyShell } from './_components/SurveyShell.js';
import { ThankYouView } from './_components/ThankYouView.js';
import { WelcomePage } from './_components/WelcomePage.js';
import { PipingProvider } from './_components/PipingProvider.js';
import { useAutosaveProgress } from './hooks/useAutosaveProgress.js';
import { useSurveyData } from './hooks/useSurveyData.js';
import { useSurveySubmission } from './hooks/useSurveySubmission.js';
import { useSurveyTracking } from './hooks/useSurveyTracking.js';
import { resolveSurveyPalette, type PublicSurvey, type SurveyQuestion } from './types.js';
import { resolveKnownEmail, shouldPromptLateIdentity } from './utils/identity.js';
import { nextPageInPath, resolveActivePath } from './utils/paths.js';
import { checkQuotas } from './utils/quotas.js';
import { resolveSurveyVariables } from './utils/variables.js';
import {
  computeNextPage,
  computeVisibilityState,
  resolveThankYouPageKey,
} from './utils/logic.js';
import {
  countContentPages,
  findContentPageIndex,
  findThankYouPageIndex,
  findWelcomePageIndex,
  groupQuestionsByPage,
  orderedPages,
  previousContentPageIndex,
} from './utils/pagination.js';

interface SurveyRendererProps {
  bridge: HostBridge;
  publicId: string;
  prefetched?: unknown;
  // When true, the host SDK is fetching the payload in parallel with the
  // iframe boot and will deliver it via chirp:survey:payload. The renderer
  // suppresses its own fetch and shows the loading state until prefetched lands.
  awaitingPayload?: boolean;
  // Surfaces the SDK's parallel-fetch failure (e.g. survey_not_found) so the
  // shell can render an inline error instead of hanging on the spinner.
  errorState?: { code: string; message: string };
  source?: 'code' | 'urlAuto';
  // Host-supplied overrides for survey variables (Chirp.survey.open opts.variables).
  variableOverrides?: Record<string, unknown>;
  hydration: ShellHydration;
}

export function SurveyRenderer({
  bridge,
  publicId,
  prefetched,
  awaitingPayload,
  errorState,
  source,
  variableOverrides,
  hydration,
}: SurveyRendererProps): JSX.Element {
  const { survey, state, errorMessage } = useSurveyData({
    hydration,
    publicId,
    prefetched: (prefetched as PublicSurvey | null) ?? null,
    awaitingPayload,
  });
  const { status, error, setError, submit, saveProgress } = useSurveySubmission({ bridge, hydration, publicId });

  const knownEmail = useMemo(() => resolveKnownEmail(hydration), [hydration]);
  const [pageIndex, setPageIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, unknown>>({});
  const [email, setEmail] = useState(knownEmail ?? '');
  const [nudgeAccepted, setNudgeAccepted] = useState(false);
  // Track which direction the user is moving so the page/question transition
  // animates from the matching side. Forward = slide up from below (or right
  // for conversational), backward = the opposite. Ref because direction is a
  // visual cue that doesn't need to trigger a re-render on its own.
  const directionRef = useRef<Direction>('forward');
  // Stack of pageIndex values navigated *from* — pushed on goNext, popped
  // on goBack. Required because path-aware traversal can jump into
  // defaultHidden pages that ordered traversal would skip, leaving no
  // trail to retrace. Held as state so canGoBack re-renders on push/pop.
  const [history, setHistory] = useState<number[]>([]);

  const palette = useMemo(() => resolveSurveyPalette(survey?.theme), [survey]);
  const pages = useMemo(() => orderedPages(survey?.pages ?? []), [survey]);
  const questionsByPage = useMemo(() => groupQuestionsByPage(survey?.questions ?? []), [survey]);
  // Conversational mode shows one question at a time across the entire
  // survey, ignoring page boundaries. We still want to skip questions that
  // live on a welcome or thank-you page (those carry copy + actions, not
  // real questions) so the flow feels continuous from intro → submit.
  const contentPageKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const page of survey?.pages ?? []) {
      if (page.isWelcome || page.isThankYou) continue;
      keys.add(page.key);
    }
    return keys;
  }, [survey?.pages]);
  const sortedQuestions = useMemo(
    () =>
      [...(survey?.questions ?? [])]
        .filter((question) => contentPageKeys.size === 0 || contentPageKeys.has(question.pageKey))
        .sort((first, second) => (first.order ?? 0) - (second.order ?? 0)),
    [survey, contentPageKeys],
  );
  const welcomeIndex = useMemo(() => findWelcomePageIndex(pages), [pages]);
  const isConversational = survey?.appearance?.displayMode === 'conversational';
  // Nudge teaser only applies to auto-triggered opens; explicit Chirp.survey.open()
  // calls (source === 'code') always go straight to the full modal.
  const showTeaser =
    source === 'urlAuto' && survey?.inApp?.displayStyle === 'nudge' && !nudgeAccepted;

  useAutosaveProgress({
    survey: survey ?? null,
    responses,
    email,
    enabled: survey?.partialSaveEnabled !== false,
    saveProgress,
  });

  // Survey variables: resolve once per survey load against URL params + the
  // host-supplied overrides. Variables flow into the logic engine (so a rule
  // can compare `variableKey: 'utm'` against 'summer') and the piping engine
  // (so labels can interpolate `{{variable.utm}}`).
  const variables = useMemo(
    () =>
      resolveSurveyVariables({
        variables: survey?.variables,
        hostUrl: hydration.host?.url,
        overrides: variableOverrides,
      }),
    [survey?.variables, hydration.host?.url, variableOverrides],
  );

  // Quota guard: re-evaluate after every response change. When a quota with
  // currentCount >= limit matches, route the user away from continuing —
  // either jump to thank-you (most common) or close the survey entirely.
  // Server still enforces on submit as the source of truth.
  useEffect(() => {
    if (!survey) return;
    const result = checkQuotas({ quotas: survey.quotas, responses, variables });
    if (!result.hit) return;
    if (result.action === 'closeSurvey') {
      dismiss('quota_full');
      return;
    }
    if (result.action === 'skipToThankYou') {
      const fallback = findThankYouPageIndex(pages);
      if (fallback >= 0) setPageIndex(fallback);
    }
  }, [responses, variables, survey?.quotas]);

  // Resolve the page + visible-question set for the analytics hook. Computed
  // here at the top level so the hook runs unconditionally — survey may not
  // be loaded yet, in which case visibleQuestions is just empty and the
  // hook holds back until the campaign id arrives.
  const trackingCurrentPage = survey ? pages[pageIndex] : undefined;
  const trackingVisibleQuestions = useMemo<SurveyQuestion[]>(() => {
    if (!survey) return [];
    if (isConversational) {
      const current = sortedQuestions[questionIndex];
      return current ? [current] : [];
    }
    if (!trackingCurrentPage || trackingCurrentPage.isWelcome || trackingCurrentPage.isThankYou) {
      return [];
    }
    const visibility = computeVisibilityState(trackingCurrentPage, survey, responses, variables);
    const pageQuestions = questionsByPage[trackingCurrentPage.key] ?? [];
    return pageQuestions.filter((q) => visibility.visibleQuestionKeys.has(q.key));
  }, [survey, isConversational, sortedQuestions, questionIndex, trackingCurrentPage, questionsByPage, responses, variables]);

  const tracking = useSurveyTracking({
    campaignId: survey?._id,
    hydration,
    visibleQuestions: trackingVisibleQuestions,
    responses,
    totalVisibleQuestions: survey?.questions?.length ?? 0,
  });

  // Set once the survey is submitted. Suppresses the survey:dismissed
  // event on subsequent close paths (X / Esc / backdrop) because closing
  // the thank-you screen is not an abandonment.
  const completedRef = useRef(false);

  // Once-per-open guard for the survey:shown emit. The effect below depends on
  // `survey` + `hydration` + `bridge` + `publicId`; if any of those references
  // change after the initial paint (notably hydration getting refreshed mid-
  // session by an identify, or React StrictMode mounting twice in dev) the
  // effect re-runs and emits a duplicate shown event. Customer analytics then
  // double-counts opens. The ref keys on `publicId` so a fresh open of a
  // different survey resets it correctly.
  const shownEmittedRef = useRef<string | null>(null);

  // Idempotency guard for the dismiss path. Once a dismiss fires for this
  // mount the iframe is being torn down by the parent, but during the
  // teardown window subsequent triggers (Esc held, double X-click, parent
  // forwarding a key the iframe also caught natively) would each emit
  // another survey:dismissed and inflate analytics. Reset is implicit —
  // a fresh open re-mounts SurveyRenderer and gets a fresh ref.
  const dismissedRef = useRef(false);

  const dismiss = (reason = 'user_close'): void => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    // After completion the close path uses reason='after_complete' so the
    // parent's bridgeRouter can suppress the survey:dismissed customer event
    // (it would otherwise inflate dismissal metrics) while still closing the
    // iframe. Tracking-side dismissed event is also skipped on this path.
    if (completedRef.current) {
      bridge.send('chirp:survey:dismissed', { surveyId: publicId, reason: 'after_complete' });
      return;
    }
    bridge.send('chirp:survey:dismissed', { surveyId: publicId, reason });
    // Pull the last visible question key so analytics can chart abandon-by-step.
    const lastKey = trackingVisibleQuestions[trackingVisibleQuestions.length - 1]?.key;
    tracking.emitDismissed(lastKey, reason);
  };

  useEffect(() => {
    if (!survey || showTeaser) return;
    // De-dupe: only fire shown once per (publicId) open. A late-arriving
    // payload, an identify-triggered hydration refresh, or StrictMode dev
    // double-mount would otherwise re-run this effect and emit a second
    // survey:shown — customer analytics depends on this being once per open.
    if (shownEmittedRef.current === publicId) return;
    shownEmittedRef.current = publicId;
    bridge.send('chirp:survey:shown', { surveyId: publicId });
    if (survey._id) {
      void trackSurveyEvent(hydration, {
        campaignId: survey._id,
        eventType: 'viewed',
        ...resolveEventIdentity(hydration),
        meta: buildEventMeta(hydration),
      });
    }
  }, [survey, showTeaser, bridge, publicId, hydration]);

  // Esc-to-dismiss. Listens at the iframe document level so any focused field
  // can yield up an Escape press. Skip if the survey hasn't loaded yet — there's
  // nothing to abandon. Single attach guarded by `survey` so React effect
  // re-runs (e.g. after submit redraws) don't pile up listeners.
  useEffect(() => {
    if (!survey) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      dismiss('esc_key');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [survey]);

  useEffect(() => {
    if (welcomeIndex >= 0 && pageIndex === 0) setPageIndex(welcomeIndex);
  }, [welcomeIndex, pageIndex]);

  if (showTeaser && survey) {
    const suppressAndClose = (): void => {
      bridge.send('chirp:survey:suppress', { surveyId: publicId, scope: 'user' });
      dismiss('nudge_suppressed');
    };
    return (
      <NudgeTeaser
        palette={palette}
        headline={survey.inApp?.nudgeHeadline}
        onAccept={() => setNudgeAccepted(true)}
        onClose={() => dismiss('nudge_closed')}
        onSuppress={suppressAndClose}
      />
    );
  }

  // Host-side error wins — SDK's parallel fetch surfaced a failure, render
  // it immediately even if the iframe would otherwise still be in 'loading'.
  if (errorState) {
    return (
      <SurveyShell palette={palette} onDismiss={() => dismiss('not_available')}>
        <StatusView
          palette={palette}
          message={
            errorState.code === 'survey_not_found'
              ? 'This survey is no longer available.'
              : errorState.message
          }
          error
          onClose={() => dismiss('not_available')}
        />
      </SurveyShell>
    );
  }
  if (state === 'loading' || !survey) {
    return (
      <SurveyShell palette={palette} onDismiss={() => dismiss()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240, padding: 28 }}>
          <Spinner palette={palette} />
        </div>
      </SurveyShell>
    );
  }
  if (state === 'error') {
    return (
      <SurveyShell palette={palette} onDismiss={() => dismiss()}>
        <StatusView palette={palette} message={errorMessage ?? 'Failed to load survey.'} error onClose={() => dismiss()} />
      </SurveyShell>
    );
  }

  const currentPage = pages[pageIndex];

  if (currentPage?.isWelcome) {
    return (
      <SurveyShell palette={palette} onDismiss={() => dismiss()} theme={survey.theme} hideBranding={survey.appearance?.hideBranding}>
        <PipingProvider responses={responses} variables={variables}>
        <WelcomePage
          palette={palette}
          page={currentPage}
          questionCount={survey.questions?.length ?? 0}
          identityConfig={survey.identityConfig}
          knownEmail={knownEmail}
          initialEmail={email}
          onStart={(collectedEmail) => {
            if (collectedEmail && collectedEmail !== email) setEmail(collectedEmail);
            const nextKey = computeNextPage(currentPage.key, survey, responses, variables);
            const nextIndex = nextKey ? pages.findIndex((page) => page.key === nextKey) : -1;
            if (nextIndex >= 0) setPageIndex(nextIndex);
          }}
        />
        </PipingProvider>
        <BrandingFooter palette={palette} theme={survey.theme} hideBranding={survey.appearance?.hideBranding} />
      </SurveyShell>
    );
  }

  if (currentPage?.isThankYou || status === 'submitted') {
    return (
      <SurveyShell palette={palette} onDismiss={() => dismiss()} theme={survey.theme} hideBranding={survey.appearance?.hideBranding}>
        <PipingProvider responses={responses} variables={variables}>
          <ThankYouView palette={palette} page={currentPage} onClose={() => dismiss()} />
        </PipingProvider>
        <BrandingFooter palette={palette} theme={survey.theme} hideBranding={survey.appearance?.hideBranding} />
      </SurveyShell>
    );
  }

  const submitSurvey = async (): Promise<void> => {
    const submitted = await submit({ survey, responses, email });
    if (!submitted) return;
    // Mark the survey as completed so the next dismiss path closes silently
    // (no phantom survey:dismissed) — see completedRef declaration.
    completedRef.current = true;
    // Submit returns boolean; the hook in useSurveySubmission already
    // recorded the responseId server-side. The completed event closes
    // the funnel and lets the dashboard compute completion rate.
    tracking.emitCompleted();
    const targetKey = resolveThankYouPageKey(currentPage, survey, responses, variables);
    const targetIndex = targetKey ? pages.findIndex((page) => page.key === targetKey) : -1;
    if (targetIndex >= 0) setPageIndex(targetIndex);
    else {
      const fallback = findThankYouPageIndex(pages);
      if (fallback >= 0) setPageIndex(fallback);
    }
  };

  if (isConversational) {
    return (
      <SurveyShell palette={palette} onDismiss={() => dismiss()} theme={survey.theme} hideBranding={survey.appearance?.hideBranding}>
        <PipingProvider responses={responses} variables={variables}>
        <ConversationalSurvey
          palette={palette}
          survey={survey}
          questions={sortedQuestions}
          questionIndex={questionIndex}
          setQuestionIndex={setQuestionIndex}
          directionRef={directionRef}
          responses={responses}
          setResponses={setResponses}
          hydration={hydration}
          submitting={status === 'submitting'}
          error={error}
          setError={setError}
          onSubmit={submitSurvey}
          onValidationFailed={tracking.emitValidationFailed}
        />
        </PipingProvider>
      </SurveyShell>
    );
  }

  const visibility = currentPage
    ? computeVisibilityState(currentPage, survey, responses, variables)
    : { visibleQuestionKeys: new Set<string>(), requiredOverrides: new Map<string, boolean>() };
  const currentQuestions = (currentPage ? questionsByPage[currentPage.key] ?? [] : []).filter(
    (question) => visibility.visibleQuestionKeys.has(question.key),
  );
  // Resolve the active path (if any) so it can override sequential traversal.
  // Higher-priority enabled paths whose entryCondition matches win — the
  // user is then routed only through that path's pageKeys, terminating at
  // exitTargetPageKey (or falling through to the default thank-you flow).
  const activePath = resolveActivePath({ paths: survey.paths, responses, variables });

  // Compute the engine's verdict on the next page — null means "end of survey, submit".
  // Path-aware traversal short-circuits the rule-based engine when active.
  const upcomingKey = currentPage
    ? activePath
      ? nextPageInPath(activePath, currentPage.key) ?? activePath.exitTargetPageKey ?? null
      : computeNextPage(currentPage.key, survey, responses, variables)
    : null;
  const lastContentPage = upcomingKey === null;
  const totalContentPages = countContentPages(pages);
  const contentPageIndex = findContentPageIndex(pages, currentPage?.key);
  const showProgress = (survey.appearance?.showProgressBar ?? false) && totalContentPages > 1;
  const lateIdentityNeeded =
    lastContentPage && welcomeIndex < 0 && shouldPromptLateIdentity(survey.identityConfig, email);
  const emailRequired = survey.identityConfig?.mode === 'emailRequired' && !email;
  const progressRatio = showProgress ? (contentPageIndex + 1) / totalContentPages : undefined;
  const pageCounter = totalContentPages > 1 ? (
    <span style={{ fontSize: 13, color: palette.textMuted }}>
      Page {contentPageIndex + 1} of {totalContentPages}
    </span>
  ) : undefined;

  const goNext = async (): Promise<void> => {
    directionRef.current = 'forward';
    if (upcomingKey === null) {
      await submitSurvey();
      return;
    }
    void saveProgress({ survey, responses, email });
    const nextIndex = pages.findIndex((page) => page.key === upcomingKey);
    if (nextIndex >= 0) {
      // Push the page we're leaving onto the back-stack so the Back button
      // retraces the actual path the user took (including jumps into
      // defaultHidden path pages that ordered traversal would skip).
      setHistory((prev) => [...prev, pageIndex]);
      setPageIndex(nextIndex);
    }
  };

  const goBack = (): void => {
    directionRef.current = 'backward';
    setError(null);
    if (history.length > 0) {
      const previous = history[history.length - 1]!;
      setHistory((prev) => prev.slice(0, -1));
      setPageIndex(previous);
      return;
    }
    // No history — fall back to ordered traversal so the Back button still
    // works when the survey was deep-linked or loaded from a saved partial.
    setPageIndex((index) => previousContentPageIndex(pages, index));
  };

  // Back button visibility: we can go back if (a) we've pushed pages onto the
  // history stack, or (b) the renderer started us on a deep page (e.g. partial
  // resume) and there's a content page before this one we could fall back to.
  const canGoBack = history.length > 0 || (
    currentPage !== undefined &&
    !currentPage.isWelcome &&
    previousContentPageIndex(pages, pageIndex) !== pageIndex
  );

  return (
    <SurveyShell palette={palette} onDismiss={() => dismiss()} progressRatio={progressRatio} theme={survey.theme} hideBranding={survey.appearance?.hideBranding}>
      <PipingProvider responses={responses} variables={variables}>
      <PagedSurvey
        palette={palette}
        survey={survey}
        currentPage={currentPage!}
        questions={currentQuestions}
        hydration={hydration}
        pageCounterSlot={pageCounter}
        lateIdentityNeeded={lateIdentityNeeded}
        emailRequired={emailRequired}
        email={email}
        setEmail={setEmail}
        responses={responses}
        setResponses={setResponses}
        canGoBack={canGoBack}
        isLast={lastContentPage}
        submitting={status === 'submitting'}
        error={error}
        setError={setError}
        onBack={goBack}
        onNext={goNext}
        direction={directionRef.current}
        onValidationFailed={tracking.emitValidationFailed}
      />
      </PipingProvider>
    </SurveyShell>
  );
}
